/**
 * Campaign API wrapper: listing, syncing from Facebook, and the three mutating
 * operations (copy, status, budget) which the server proxies to the Graph API
 * before persisting.
 */
import { api } from '@/utils/api';
import type {
  BulkActionResult,
  CampaignDTO,
  CampaignListResponse,
  CopyCampaignInput,
  DashboardSummary,
  DeployFromTemplateInput,
  DeployFromTemplateResult,
  SheetRevenueSyncResult,
  SyncInsightsResult,
  SyncResult,
  UpdateBudgetInput,
} from '@shared/types';

/** Filter params accepted by {@link list}. */
export interface CampaignListParams {
  /** Single ad account (internal id). Legacy; prefer {@link adAccountIds}. */
  adAccountId?: string;
  /** Multiple ad accounts (internal ids). Empty/undefined = all accounts. */
  adAccountIds?: string[];
  status?: string;
  /** Keep only campaigns that actually spent in the range (spend > 0). */
  spending?: boolean;
  /** Keep only campaigns marked as templates. */
  template?: boolean;
  /** Insights date range (YYYY-MM-DD). Defaults to the last 30 days server-side. */
  from?: string;
  to?: string;
  /** Bypass the server's 5-minute insights cache. */
  refresh?: boolean;
}

/** Join selected account ids into the comma-separated `ad_account_ids` param. */
function accountIdsParam(params?: CampaignListParams): string | undefined {
  const ids = params?.adAccountIds?.filter(Boolean) ?? [];
  if (params?.adAccountId) ids.push(params.adAccountId);
  return ids.length ? ids.join(',') : undefined;
}

/** List campaigns (+ aggregate totals) with per-range insights, filtered by ad account/status. */
export async function list(params?: CampaignListParams): Promise<CampaignListResponse> {
  const response = await api.get<CampaignListResponse>('/campaigns', {
    params: {
      ad_account_ids: accountIdsParam(params),
      status: params?.status,
      spending: params?.spending ? 1 : undefined,
      template: params?.template ? 1 : undefined,
      from: params?.from,
      to: params?.to,
      refresh: params?.refresh ? 1 : undefined,
    },
  });
  return response.data;
}

/** Params for {@link summary}. */
export interface DashboardSummaryParams {
  adAccountIds?: string[];
  from?: string;
  to?: string;
}

/** Business-results summary for the dashboard (revenue / ad cost / profit / ROAS). */
export async function summary(params?: DashboardSummaryParams): Promise<DashboardSummary> {
  const ids = params?.adAccountIds?.filter(Boolean) ?? [];
  const response = await api.get<DashboardSummary>('/campaigns/summary', {
    params: {
      ad_account_ids: ids.length ? ids.join(',') : undefined,
      from: params?.from,
      to: params?.to,
    },
  });
  return response.data;
}

/** Sync campaigns for an ad account from the Facebook Graph API. */
export async function sync(adAccountId: string): Promise<SyncResult> {
  const response = await api.post<SyncResult>('/campaigns/sync', { adAccountId });
  return response.data;
}

/** Refresh the 30-day performance snapshot (spend/CPA/ROAS/conversions). */
export async function syncInsights(adAccountId?: string): Promise<SyncInsightsResult> {
  const response = await api.post<SyncInsightsResult>('/campaigns/insights/sync', { adAccountId });
  return response.data;
}

/** Pull real paid-order revenue from the LifeOS360 sheet and attribute it by route. */
export async function syncSheetRevenue(): Promise<SheetRevenueSyncResult> {
  const response = await api.post<SheetRevenueSyncResult>('/revenue/sync-sheet');
  return response.data;
}

/** Duplicate an existing campaign (optionally under a new name). */
export async function copy(id: string, input: CopyCampaignInput): Promise<CampaignDTO> {
  const response = await api.post<CampaignDTO>(`/campaigns/${id}/copy`, input);
  return response.data;
}

/**
 * Recreate a template campaign from scratch into one or more target accounts.
 * This creates a full campaign (campaign + ad sets + ads) per account × copies
 * and can take ~10-15s each, so it needs a much longer timeout than the default.
 */
export async function deployFromTemplate(
  input: DeployFromTemplateInput,
): Promise<DeployFromTemplateResult> {
  const response = await api.post<DeployFromTemplateResult>('/campaigns/deploy-from-template', input, {
    timeout: 900_000, // up to 15 min for large multi-account × multi-copy deploys
  });
  return response.data;
}

/**
 * "Làm mới tất cả" — one call that refreshes revenue + ad accounts + campaigns
 * (incl. new ones) + insights on the server. Slow; the UI shows a spinner.
 */
export async function refreshAll(): Promise<{ message: string }> {
  const response = await api.post<{ message: string }>('/campaigns/refresh-all', {}, { timeout: 900_000 });
  return response.data;
}

/** Mark or unmark a campaign as a reusable template. */
export async function setTemplate(id: string, isTemplate: boolean): Promise<CampaignDTO> {
  const response = await api.put<CampaignDTO>(`/campaigns/${id}/template`, { isTemplate });
  return response.data;
}

/** Bulk set ACTIVE/PAUSED on many campaigns. */
export async function bulkStatus(ids: string[], status: 'ACTIVE' | 'PAUSED'): Promise<BulkActionResult> {
  const response = await api.post<BulkActionResult>('/campaigns/bulk/status', { ids, status });
  return response.data;
}

/** Bulk set daily budget on many campaigns. */
export async function bulkBudget(ids: string[], dailyBudget: number): Promise<BulkActionResult> {
  const response = await api.post<BulkActionResult>('/campaigns/bulk/budget', { ids, dailyBudget });
  return response.data;
}

/** Bulk rename campaigns; each item carries the pre-computed new name. */
export async function bulkRename(items: Array<{ id: string; name: string }>): Promise<BulkActionResult> {
  const response = await api.post<BulkActionResult>('/campaigns/bulk/rename', { items });
  return response.data;
}

/** Turn a campaign on or off. */
export async function updateStatus(
  id: string,
  status: 'ACTIVE' | 'PAUSED',
): Promise<CampaignDTO> {
  const response = await api.put<CampaignDTO>(`/campaigns/${id}/status`, { status });
  return response.data;
}

/** Update a campaign's daily and/or lifetime budget. */
export async function updateBudget(id: string, input: UpdateBudgetInput): Promise<CampaignDTO> {
  const response = await api.put<CampaignDTO>(`/campaigns/${id}/budget`, input);
  return response.data;
}

export default {
  list,
  summary,
  sync,
  syncInsights,
  refreshAll,
  copy,
  deployFromTemplate,
  setTemplate,
  bulkStatus,
  bulkBudget,
  bulkRename,
  updateStatus,
  updateBudget,
};
